<template>
    <div class="visual-online-profile-widget">
        <div class="metric-selector" v-if="showMetricSelector">
            <select v-model="selectedMetric" class="metric-selects" @change="onMetricChange">
                <option value="likes">Likes</option>
                <option value="followerCount">Followers</option>
            </select>
        </div>

        <div v-if="error" class="error-state">
            <p class="error-message">{{ error }}</p>
        </div>

        <div v-else-if="!chartData || chartData.length === 0" class="empty-state">
            <p>{{ emptyMessage }}</p>
        </div>

        <div v-else class="chart-container" ref="chartElement"></div>
    </div>
</template>

<script>
import * as b3js from 'brandseyejs'
import moment from 'moment'
import {formatNumber, formatPercentage} from '@/app/utils/Format'
import {currentAccountCode} from '@/app/utils/Account'
import './visual/VisualOnlineProfile.css'

export default {
    name: 'VisualOnlineProfile',
    props: {
        visualData: {
            type: Array,
            default: () => []
        },
        metadata: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            /**
             * @type {OnlineProfileStats[]}
             */
            chartData: [],
            loading: true,
            error: null,
            selectedMetric: 'followerCount',
            showMetricSelector: false,
            emptyMessage: 'Your filter has no profile information available',
            accountCode: currentAccountCode(),
            chart: null,
            resizeObserver: null,
            isRendering: false
        }
    },
    watch: {
        selectedMetric() {
            this.drawChart()
        },
        visualData: {
            handler(newData) {
                this.chartData = newData
                this.updateMetricSelectorVisibility()
                this.drawChart()
            },
            deep: true,
            immediate: true
        },
        metadata: {
            handler(newData) {
            }
        }
    },
    mounted() {
        this.initChart()

        // Set up resize observer with debouncing to prevent infinite loops
        let resizeTimeout
        this.resizeObserver = new ResizeObserver(() => {
            clearTimeout(resizeTimeout)
            resizeTimeout = setTimeout(() => {
                this.redraw()
            }, 250)
        })

        const parentElement = this.$el.closest('.widget-height-inner') || this.$el.parentElement
        if (parentElement) {
            this.resizeObserver.observe(parentElement)
        }
    },

    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
    },
    methods: {
        initChart() {
            this.chart = new b3js.chart()
        },

        updateMetricSelectorVisibility() {
            this.showMetricSelector = this.chartData && this.chartData.length > 0
        },

        onMetricChange() {
            // Redraw chart with new metric
            this.drawChart()
        },

        drawChart() {
            if (this.isRendering) {
                return
            }
            if (!this.chartData || this.chartData.length === 0) {
                this.$refs.chartElement.innerHTML = ''
                return
            }

            this.isRendering = true
            this.loading = true

            // using setTimeout to give the component time to render the loading state
            setTimeout(() => {
                this.$nextTick(() => {
                    if (!this.$refs.chartElement) {
                        this.isRendering = false
                        this.loading = false
                        return
                    }

                    try {
                    const chartContainer = this.$refs.chartElement
                    const selectedMetric = this.selectedMetric

                    // Calculate available space accounting for borders and padding
                    const computedStyle = window.getComputedStyle(chartContainer)
                    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0
                    const paddingRight = parseFloat(computedStyle.paddingRight) || 0
                    const paddingTop = parseFloat(computedStyle.paddingTop) || 0
                    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
                    const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0
                    const borderRight = parseFloat(computedStyle.borderRightWidth) || 0
                    const borderTop = parseFloat(computedStyle.borderTopWidth) || 0
                    const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0

                    const containerWidth = chartContainer.clientWidth - paddingLeft - paddingRight - borderLeft - borderRight
                    const containerHeight = chartContainer.clientHeight - paddingTop - paddingBottom - borderTop - borderBottom

                    // Ensure chart instance exists
                    if (!this.chart) {
                        this.chart = new b3js.chart()
                    }

                    // Flatten the data structure for b3js
                    const flatData = []
                    this.chartData.forEach(series => {
                        series.values.forEach(point => {
                            flatData.push({
                                ...point,
                                seriesKey: series.key
                            })
                        })
                    })

                    this.chart
                        .reset()
                        .geometry(b3js.line())
                        .scaleX(b3js.scaleTime())
                        .data(flatData)
                        .element(chartContainer)
                        .x(d => {
                            if (d.captured instanceof Date) {
                                return d.captured
                            }

                            if (typeof d.captured === 'string') {
                                return new Date(d.captured)
                            }

                            return new Date(d.captured)
                        })
                        .y(d => {
                            if (selectedMetric === 'likes' && d.likesDelta !== undefined) {
                                return d.likesDelta
                            }

                            if (selectedMetric === 'followerCount' && d.followerCountDelta !== undefined) {
                                return d.followerCountDelta
                            }
                            return 0
                        })
                        .colour(d => d.seriesKey)
                        .width(containerWidth)
                        .height(containerHeight)
                        .showXAxis(true)
                        .showYAxis(true)
                        .fontSize(this.metadata.fontSize)
                        .formatY(val => {
                            if (this.metadata.numberFormat === '9 200') {
                                return formatNumber(val);
                            } else {
                                return (val / 1000).toFixed(1) + 'k';
                            }
                        })
                        .yAxisLabel(this.getYAxisLabel())
                        .formatX((val, i) => {

                            const date = moment(val)

                            switch (this.metadata.dateFormat) {

                                case '29': {
                                    return date.format('DD');
                                }

                                case 'Sat 29': {
                                    return date.format('ddd DD');
                                }

                                case 'Feb 29': {
                                    return date.format('MMM DD');
                                }

                                case 'Feb': {
                                    return date.format('MMM');
                                }

                                case 'Hidden': {
                                    return '';
                                }

                                // also handles 'Auto'
                                default: {
                                    // First date: show full date
                                    if (i === 0) {
                                        return date.format('MMM DD')
                                    }
                                    // First of the month: show full date
                                    if (date.date() === 1) {
                                        return date.format('MMM DD')
                                    }
                                    // All other dates: show just the day number
                                    return date.format('DD')
                                }
                            }
                        })

                        this.chart.render()
                    } catch (err) {
                        this.error = 'Failed to render chart: ' + err.message
                    } finally {
                        this.isRendering = false
                        this.loading = false
                    }
                })
            }, 0)
        },

        getYValue(point) {
            if (this.selectedMetric === 'likes' && point.likesDelta !== undefined) {
                return point.likesDelta
            }
            if (this.selectedMetric === 'followerCount' && point.followerCountDelta !== undefined) {
                return point.followerCountDelta
            }
            return 0
        },

        getYAxisLabel() {
            if (this.selectedMetric === 'followerCount') {
                return 'Change in Followers for Online Profiles'
            }
            return 'Change in Likes for Online Profiles'
        },

        redraw() {
            if (this.$refs.chartElement) {
                this.$refs.chartElement.innerHTML = ''
            }
            this.drawChart()
        }
    }
}
</script>

<style scoped lang="sass">
.visual-online-profile-widget
    display: flex
    flex-direction: column
    gap: 12px
    height: 100%
    width: 100%
    box-sizing: border-box

.metric-selector
    display: flex
    gap: 10px
    margin-top: 2px
    padding: 0
    justify-content: center
    position: relative

.metric-selector .metric-selects
    width: 91px
    z-index: 1
    border: 1px solid #ccc
    outline: none
    padding: 4px 8px
    border-radius: 4px
    cursor: pointer
    position: absolute
    top: -20px

.metric-selector .metric-selects:focus
    outline: none
    border: 1px solid #4a90e2

.chart-container
    width: 100%
    height: 100%
    background: #fff
    border: 1px solid #e0e0e0
    border-radius: 4px
    position: relative
    box-sizing: border-box

    svg
        display: block

.loading-state, .error-state, .empty-state
    display: flex
    align-items: center
    justify-content: center
    min-height: 300px
    flex-direction: column
    gap: 12px

.loading-state
    color: #666

.spinner
    width: 32px
    height: 32px
    border: 3px solid #e0e0e0
    border-top-color: #4a90e2
    border-radius: 50%
    animation: spin 0.6s linear infinite

@keyframes spin
    to
        transform: rotate(360deg)

.error-state
    color: #d32f2f
    text-align: center

.error-message
    margin: 0

.empty-state
    color: #999
    text-align: center
</style>
